import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { jwtDecode } from "jwt-decode";

import { supabase } from "@/lib/supabase";
import {
  authorizationUser,
  fetchBrandRegister,
  fetchCompetitorsRegister,
  fetchPaymentStatus,
  fetchPaymentStatusFailed,
  fetchPaymentStatusSuccess,
  fetchRelatedBrands,
  fetchRelatedBrandsFailed,
  fetchRelatedBrandsSuccess,
  forgotPasswordUser,
  getStripeCustomerPortal,
  loginUser,
  loginUserFailed,
  loginUserSuccess,
  logoutUser,
  logoutUserSuccess,
  oAuthUser,
  refreshSession,
  registerUser,
  registerUserFailed,
  registerUserSuccess,
  resetPasswordUser,
  resetPasswordUserFailed,
  resetPasswordUserSuccess,
  setBrand,
  setCompetitorsRegister,
  setAgencyInfo,
  setMemberInfo,
  clearActingAsOnUserChange,
  setLoginUserLoading,
  setPaymentStatusLoading,
  setPlan,
  setRegisterUserLoading,
  setRelatedBrandsLoading,
  setResetPasswordUserLoading,
  setRole,
  setSelectedCompetitorsRegister,
  setUpdateUserLoading,
  setUser,
  updatePasswordUser,
  updatePasswordUserFailed,
  updatePasswordUserSuccess,
  updateUser,
  updateUserFailed,
  updateUserSuccess,
  upgradeSubscription,
} from "./slice";
import {
  AuthorizationUserPayload,
  FetchPaymentStatusPayload,
  FetchRelatedBrandsPayload,
  ForgotPasswordPayload,
  JWT,
  LoginPayload,
  OAuthPayload,
  RegisterOAuth,
  RegisterPayload,
  ResetPasswordPayload,
  Role,
  UpdatePasswordPayload,
  UpdateUserPayload,
  UserStatus,
} from "./types";
import {
  selectAuthCompany,
  selectAuthPlan,
  selectAuthCompetitorsRegister,
  selectAuthSelectedCompetitorsRegister,
  selectAuthUser,
} from "../selectors/auth";
import { DEFAULT_AUTHENTICATED_ROUTE, ERoutes } from "../../constants/routes";
import {
  applySagaError,
  createInitBrandsSaga,
  identifyUserSaga,
  reserchScoresSaga,
  updateUserDataSaga,
} from "../utils/saga";
import { User } from "../../types/auth";
import { LogoResponse } from "../brands/types";
import { startLoading, stopLoading } from "../common/slice";
import { mapAuthError } from "./utils/mapAuthError";
import { mapperInitBrandsData } from "./utils/mapperInitBrandsData";
import {
  clearRegisterOAuthStorage,
  saveRegisterOAuthStorage,
  readRegisterOAuthStorage,  
} from "./utils/registerOAuthStorage";
import { API_ENDPOINTS } from "../../constants/api";
import { Company } from "./types";
import analytics from "@/lib/Analytics";
import { setSelectedBrandId } from "../brands/slice";
import { CompetitorWithLogo } from "../../types/competitor";
import { apiGet } from "../../utils/apiClient";
import { fetchAccountContext } from "../../api/accountContext";
import {
  DOMAIN_ERROR_CODES,
  DomainError,
  SupabaseRequestError,
  throwOnSupabaseError,
} from "../../utils/errors";
import { SubscriptionPlan } from "../../constants/plans";
import { getStoredPlanOrDefault } from "../../utils/getStoredPlanOrDefault";
import { createRegisterOAuthQuery, parseRegisterOAuthQuery } from "./utils/registerOAuthQuery";
import { upgradeSubscriptionSaga } from "./sagas/upgradeSubscriptionSaga";
import { AccountContextResult } from "../../api/accountContext";
import { fetchBrandRegisterSaga } from "./sagas/fetchBrandRegisterSaga";
import { fetchCompetitorsRegisterSaga } from "./sagas/fetchCompetitorsRegisterSaga";

function* clearActingAsIfUserChanged(userId: string) {
  const previousUser: User | null = yield select(selectAuthUser);
  yield put(clearActingAsOnUserChange({
    previousUserId: previousUser?.id ?? null,
    userId,
  }));
}

/**
 * Resolves team membership context and dispatches it to the store.
 * Reused across register, OAuth, and login flows.
 */
function* resolveAndSetMemberContext(userId: string) {
  // GET /v1/account → определяет роль (owner/member), accountId и is_agency
  const memberCtx: AccountContextResult = yield call(fetchAccountContext, userId);
  // Сохраняем isMember + accountId (owner's user_id) в auth store
  yield put(setMemberInfo(memberCtx));
  // Сохраняем флаг агентства (MCC) в auth store
  yield put(setAgencyInfo({ isAgency: memberCtx.isAgency }));
  return memberCtx;
}


export function* fetchRelatedBrandsSaga(
  action: PayloadAction<FetchRelatedBrandsPayload>,
) {
  try {
    yield put(setRelatedBrandsLoading());
    const { brand } = action.payload;

    const response: LogoResponse[] = yield call(
      apiGet<LogoResponse[]>,
      API_ENDPOINTS.logoSearch(brand), { auth: "logoApi" });

    if (!Array.isArray(response)) {
      throw new DomainError(
        DOMAIN_ERROR_CODES.VALIDATION_FAILED,
        "Logo API response is not an array",
      );
    }

    yield put(fetchRelatedBrandsSuccess(response));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.fetchRelatedBrands",
      failureAction: fetchRelatedBrandsFailed,
    });
  }
}

export function* registerUserSaga(action: PayloadAction<RegisterPayload>) {
  try {
    yield put(setRegisterUserLoading());

    const plan: SubscriptionPlan = yield select(selectAuthPlan);

    const {
      email,
      password,
      firstName,
      lastName,
    }: RegisterPayload = action.payload;

    const { data, error } = yield call([supabase.auth, "signUp"], {
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`,
        },
      },
    });

    throwOnSupabaseError(error);

    if (!data?.user) {
      throw new DomainError(
        DOMAIN_ERROR_CODES.AUTH_REGISTRATION_NO_USER,
        "The user's data was not received after registration.",
      );
    }

    yield registerInitDataSaga(data.user);

    analytics.track("User registered", { plan });
    analytics.setUserProperties({ selected_plan: plan });

    const paymentLink: string = yield call(getStripePaymentLinkSaga, data.user.email, data.user.id, plan);

    if (paymentLink) {
      yield call(() => window.location.assign(paymentLink));
    } else {
      yield call(() => window.location.assign(`${window.location.origin}${DEFAULT_AUTHENTICATED_ROUTE}`));
    }

    yield put(registerUserSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.registerUser",
      failureAction: registerUserFailed,
    });
  }
}

export function* registerInitDataSaga(user: User) {
  try {
    yield put(setRegisterUserLoading());

    // Check if the registering user is a team member (invited by an owner).
    // Members share the owner's brands/scores — they must NOT create brands
    // or trigger score calculation under their own user_id.
    const memberCtx: AccountContextResult = yield* resolveAndSetMemberContext(user.id);

    if (memberCtx.isMember) {
      // Member: skip brand creation & score calculation — they use the owner's data.
      yield call(identifyUserSaga, user);
      yield call(clearRegisterOAuthStorage);
      return;
    }

    const company: Company | null = yield select(selectAuthCompany);
    if (!company) {
      throw new DomainError(
        DOMAIN_ERROR_CODES.VALIDATION_FAILED,
        "Registration brand data is missing after sign-in. Please start registration again.",
      );
    }

    const competitorsRaw: CompetitorWithLogo[] | null = yield select(selectAuthCompetitorsRegister);
    const selectedRaw: CompetitorWithLogo[] | null = yield select(selectAuthSelectedCompetitorsRegister);
    const competitors = competitorsRaw ?? [];
    const selectedCompetitors = selectedRaw ?? [];

    yield call(identifyUserSaga, user, company.name);

    yield call(
      createInitBrandsSaga,
      mapperInitBrandsData(user.id, company, competitors, selectedCompetitors),
    );

    yield call(reserchScoresSaga, user.id, true);
    yield call(clearRegisterOAuthStorage);  
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.registerInitData",
      failureAction: registerUserFailed,
      captureStoreSnapshot: true,
    });
  }
}

export function* oAuthUserSaga(action: PayloadAction<OAuthPayload>) {
  try {
    const { type, isRegister }: OAuthPayload = action.payload;
    let redirectTo: string = window.location.origin;

    if (isRegister) {
      yield put(setRegisterUserLoading());
    } else {
      yield put(setLoginUserLoading());
    }

    if (isRegister) {
      const company: Company | null = yield select(selectAuthCompany);
      const competitors: CompetitorWithLogo[] | null = yield select(selectAuthCompetitorsRegister);
      const selectedCompetitors: CompetitorWithLogo[] | null = yield select(selectAuthSelectedCompetitorsRegister);
      const plan: SubscriptionPlan = yield select(selectAuthPlan);

      const oauth: RegisterOAuth = {
        company,
        competitors,
        selectedCompetitors,
        plan,
      };

      yield call(saveRegisterOAuthStorage, oauth);

      const query = createRegisterOAuthQuery({
        company,
        competitors,
        selectedCompetitors,
        plan,
      });

      redirectTo += `${ERoutes.Register}?${query}`;
    } else {
      redirectTo += ERoutes.Login;
    }

    const { error } = yield call([supabase.auth, "signInWithOAuth"], {
      provider: type,
      options: {
        redirectTo,
        ...(type === "azure" && { scopes: "email" }),
      },
    });

    throwOnSupabaseError(error);
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.oAuthUser",
      failureAction: registerUserFailed,
    });
  }
}

export function* refreshSessionSaga() {
  try {
    yield call([supabase.auth, 'refreshSession']);
  } catch (error) {
    yield* applySagaError(error, { operationName: "auth.refreshSession" });
  }
}

export function* authorizationUserSaga(
  action: PayloadAction<AuthorizationUserPayload>,
) {
  try {
    const { isRegister, userRole, isSubscribed, user, selectedBrandId }:
      AuthorizationUserPayload = action.payload;

    const isResetPassword = location.pathname.includes(ERoutes.ResetPassword);

    // Make one shared Upload to wait for data
    if (isRegister) {
      yield put(setRegisterUserLoading());
    } else {
      yield put(setLoginUserLoading());
    }
    // ====================================

    if (user.app_metadata.provider !== "email") {
      // If an unregistered user logs in via OAuth,
      // we check for the brand. If it is not found,
      // then the user is not registered.
      const { data } = yield call(() =>
        supabase
          .from("brands")
          .select("*")
          .eq("user_id", user.id)
          .eq("competitor", false)
          .is("deleted_at", null)
      );
      // =========================

      if (isRegister) {
        yield call(handleRegisterFlow, !!data.length, user, userRole as Role, selectedBrandId);
      } else {
        yield call(handleLoginFlow, !!data.length, user, userRole as Role, isSubscribed);
      }
    } else {
      yield put(setRole(userRole as Role));
      yield* clearActingAsIfUserChanged(user.id);
      yield put(setUser(user));

      if (selectedBrandId) {
        yield put(setSelectedBrandId(selectedBrandId));
      }

      // Check if user is a team member — members use the owner's subscription
      const memberCtx: AccountContextResult = yield* resolveAndSetMemberContext(user.id);

      if (!isResetPassword) {
        if (memberCtx.isMember) {
          // Members inherit the owner's subscription — skip payment checks
          yield put(fetchPaymentStatusSuccess("paid"));
        } else if (memberCtx.isManagedClient) {
          // Managed agency clients — agency pays for them, skip payment checks
          yield put(fetchPaymentStatusSuccess("paid"));
        } else if (!isSubscribed) {
          const paymentStatus: boolean = yield call(
            fetchPaymentStatusSaga,
            { payload: { email: user.email }, type: fetchPaymentStatus.type },
          );

          if (!paymentStatus) {
            const paymentLink: string = yield call(getStripePaymentLinkSaga, user.email, user.id, getStoredPlanOrDefault());
            if (paymentLink) {
              yield call(() => window.location.assign(paymentLink));
            }
          } else {
            yield call(refreshSessionSaga);
            yield put(fetchPaymentStatusSuccess("paid"));
          }
        } else {
          yield put(fetchPaymentStatusSuccess("paid"));
        }
      }

      yield put(loginUserSuccess());
    }
  } catch (error) {
    yield call(logoutUserSaga);
    yield* applySagaError(error, {
      operationName: "auth.authorizationUser",
      failureAction: loginUserFailed,
    });
  }
}

function* hydrateRegisterOAuthSaga() {
  const queryParams = new URLSearchParams(window.location.search);
  const query: RegisterOAuth | null = yield call(parseRegisterOAuthQuery, queryParams.toString());
  const draft: RegisterOAuth | null = yield call(readRegisterOAuthStorage);

  const plan = draft?.plan ?? query?.plan ?? null;
  const company = query?.company ?? draft?.company ?? null;
  const competitors = draft?.competitors ?? query?.competitors ?? null;
  const selectedCompetitors = draft?.selectedCompetitors ?? query?.selectedCompetitors ?? null;

  if (company) yield put(setBrand(company));
  if (plan) yield put(setPlan(plan)); 
  if (competitors) yield put(setCompetitorsRegister(competitors));
  if (selectedCompetitors) yield put(setSelectedCompetitorsRegister(selectedCompetitors));
}

function* handleRegisterFlow(
  hasData: boolean,
  user: User,
  userRole: Role,
  selectedBrandId: number | undefined,
) {
  try {
    if (hasData) {
      yield call(logoutUserSaga, false);
      throw new DomainError(
        DOMAIN_ERROR_CODES.AUTH_USER_ALREADY_REGISTERED,
        "User already registered",
      );
    }

    // Mirror email branch in authorizationUserSaga: persist user/role in Redux before post-auth work
    yield put(setRole(userRole));
    yield* clearActingAsIfUserChanged(user.id);
    yield put(setUser(user));
    
    if (selectedBrandId) {
      yield put(setSelectedBrandId(selectedBrandId));
    }

    yield* hydrateRegisterOAuthSaga();
    yield call(registerInitDataSaga, user);

    const plan: SubscriptionPlan = yield select(selectAuthPlan);
    const paymentLink: string = yield call(getStripePaymentLinkSaga, user.email, user.id, plan);

    if (paymentLink) {
      yield call(() => window.location.assign(paymentLink));
    } else {
      yield call(() => window.location.assign(`${window.location.origin}${DEFAULT_AUTHENTICATED_ROUTE}`));
    }

    yield put(registerUserSuccess());
  } catch (error) {
    yield call(logoutUserSaga, false);
    const shouldCaptureInSentry = !(
      error instanceof DomainError &&
      error.code === DOMAIN_ERROR_CODES.AUTH_USER_ALREADY_REGISTERED
    );
    yield* applySagaError(error, {
      operationName: "auth.handleRegisterFlow",
      failureAction: registerUserFailed,
      captureInSentry: shouldCaptureInSentry,
    });
  }
}

function* handleLoginFlow(
  hasData: boolean,
  user: User,
  userRole: Role,
  isSubscribed: boolean | undefined,
) {
  try {
    if (!hasData) {
      yield call(logoutUserSaga);
      throw new DomainError(
        DOMAIN_ERROR_CODES.AUTH_ACCOUNT_NOT_REGISTERED,
        "The account is not registered",
      );
    }

    yield put(setRole(userRole as Role));
    yield* clearActingAsIfUserChanged(user.id);
    yield put(setUser(user));
    yield call(identifyUserSaga, user);

    // Check if user is a team member — members use the owner's subscription
    const memberCtx: AccountContextResult = yield* resolveAndSetMemberContext(user.id);

    if (memberCtx.isMember) {
      // Members inherit the owner's subscription — skip payment checks
      yield put(fetchPaymentStatusSuccess("paid"));
    } else if (memberCtx.isManagedClient) {
      // Managed agency clients — agency pays for them, skip payment checks
      yield put(fetchPaymentStatusSuccess("paid"));
    } else if (!isSubscribed) {
      const paymentStatus: boolean | undefined = yield call(
        fetchPaymentStatusSaga,
        { payload: { email: user.email }, type: fetchPaymentStatus.type },
      );

      if (!paymentStatus) {
        const paymentLink: string = yield call(
          getStripePaymentLinkSaga,
          user.email,
          user.id,
          getStoredPlanOrDefault(),
        );
        if (paymentLink) {
          yield call(() => window.location.assign(paymentLink));
        }
      } else {
        yield call(refreshSessionSaga);
        yield put(fetchPaymentStatusSuccess("paid"));
      }
    } else {
      yield put(fetchPaymentStatusSuccess("paid"));
    }

    yield put(loginUserSuccess());
  } catch (error) {
    yield call(logoutUserSaga);
    const shouldCaptureInSentry = !(
      error instanceof DomainError &&
      error.code === DOMAIN_ERROR_CODES.AUTH_ACCOUNT_NOT_REGISTERED
    );
    yield* applySagaError(error, {
      operationName: "auth.handleLoginFlow",
      failureAction: loginUserFailed,
      captureInSentry: shouldCaptureInSentry,
    });
  }
}

export function* loginUserSaga(action: PayloadAction<LoginPayload>) {
  try {
    yield put(setLoginUserLoading());
    const { email, password }: LoginPayload = action.payload;

    const { error, data } = yield call([
      supabase.auth,
      "signInWithPassword",
    ], {
      email,
      password,
    });

    if (error) {
      if (error.code === "invalid_credentials") {
        throw new DomainError(DOMAIN_ERROR_CODES.AUTH_INVALID_CREDENTIALS, mapAuthError(error), {
          cause: error,
        });
      }
      throw new SupabaseRequestError(mapAuthError(error), { cause: error });
    }

    if (!data?.user || !data?.session) {
      throw new DomainError(
        DOMAIN_ERROR_CODES.AUTH_LOGIN_NO_SESSION,
        "User data or session was not received after login.",
      );
    }

    const { user, session } = data;

    const jwt: JWT = jwtDecode(session.access_token);
    const { user_role, is_subscribed } = jwt;

    yield call(identifyUserSaga, user);

    yield put(authorizationUser({
      user,
      isRegister: false,
      isSubscribed: is_subscribed === UserStatus.Subscribed,
      userRole: user_role,
    }));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.loginUser",
      failureAction: loginUserFailed,
    });
  }
}

export function* fetchPaymentStatusSaga(action: PayloadAction<FetchPaymentStatusPayload>) {
  try {
    yield put(setPaymentStatusLoading()); 

    const {data, error} = yield call(() =>
      supabase.rpc("is_subscribed", { p_email: action.payload.email })
    );

    throwOnSupabaseError(error);

    return data;
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.fetchPaymentStatus",
      failureAction: fetchPaymentStatusFailed,
    });
  }
}

export function* logoutUserSaga(resetState: boolean = true) {
  try {
    yield put(startLoading());
    const { error } = yield call([supabase.auth, "signOut"]);
    throwOnSupabaseError(error);

    yield put(logoutUserSuccess());

    if (resetState) {
      yield put({ type: "RESET_STATE" });
    }
  } catch (error) {
    yield* applySagaError(error, { operationName: "auth.logoutUser" });
  } finally {
    yield put(stopLoading());
  }
}

export function* getStripeCustomerPortalSaga() {
  try {
    yield put(startLoading());
    const { email }: User = yield select(selectAuthUser);

    const { data, error } = yield call(() =>
      supabase.functions.invoke("get-stripe-customer-portal", {
        body: JSON.stringify({ email, returnUrl: window.location.href }),
      })
    );

    throwOnSupabaseError(error);

    yield call(() => {
      window.location.href = data.url;
      return new Promise(() => { });
    });
  } catch (error) {
    yield* applySagaError(error, { operationName: "auth.getStripeCustomerPortal" });
  } finally {
    yield put(stopLoading());
  }
}

export function* getStripePaymentLinkSaga(  
  email: string,
  userId: string,
  plan: string,
) {
  try {
    const [planName, billingPeriod] = plan.split('-');

    analytics.track('Checkout Started', {
      plan: plan,
      plan_tier: planName,
      billing_period: billingPeriod
    });

    analytics.setUserProperties({ selected_plan: plan });

    const { data, error } = yield call(() =>
      supabase.functions.invoke("get-stripe-payment-link", {
        body: JSON.stringify({ email, user_id: userId, plan }),
      })
    );

    throwOnSupabaseError(error);

    if (!data?.url) {
      throw new DomainError(
        DOMAIN_ERROR_CODES.AUTH_PAYMENT_LINK_MISSING,
        "Payment link URL missing in response",
      );
    }

    return data.url;
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.getStripePaymentLink",
      sentryContext: {
        operation: "get-stripe-payment-link",
      },
    });
  }
}

export function* updateUserSaga(action: PayloadAction<UpdateUserPayload>) {
  try {
    yield put(setUpdateUserLoading());
    const { firstName, lastName }: UpdateUserPayload = action.payload;

    const user: User = yield call(updateUserDataSaga, {
      first_name: firstName,
      last_name: lastName,
      full_name: `${firstName} ${lastName}`,
    });

    yield put(setUser(user));
    yield put(updateUserSuccess());

    const changedFields: string[] = [];
    // We can assume names changed if we are here, or compare with previous state if we had access to it before update
    // But since payload has firstName/lastName, we can check if they are present
    if (firstName) changedFields.push('firstName');
    if (lastName) changedFields.push('lastName');

    analytics.track('Profile Updated', {
      changed_fields: changedFields
    });
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.updateUser",
      failureAction: updateUserFailed,
    });
  }
}

export function* resetPasswordUserSaga(action: PayloadAction<ResetPasswordPayload>) {
  try {
    yield put(setResetPasswordUserLoading());
    const { password }: ResetPasswordPayload = action.payload;

    const { error } = yield call(() => supabase.auth.updateUser({ password }));

    throwOnSupabaseError(error);

    yield put(resetPasswordUserSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.resetPassword",
      failureAction: resetPasswordUserFailed,
    });
  }
}

export function* updatePasswordUserSaga(action: PayloadAction<UpdatePasswordPayload>) {
  try {
    const { oldPassword, password }: UpdatePasswordPayload = action.payload;

    yield put(setUpdateUserLoading());

    const { error } = yield call(() =>
      supabase.rpc("change_user_password", {
        current_plain_password: oldPassword,
        new_plain_password: password,
      })
    );
    throwOnSupabaseError(error);

    yield put(updatePasswordUserSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.updatePassword",
      failureAction: updatePasswordUserFailed,
    });
  }
}

export function* forgotPasswordUserSaga(action: PayloadAction<ForgotPasswordPayload>) {
  try {
    const { email }: ForgotPasswordPayload = action.payload;

    yield put(setResetPasswordUserLoading());

    const { error } = yield call(() =>
      supabase.auth.resetPasswordForEmail(email)
    );

    throwOnSupabaseError(error);

    yield put(resetPasswordUserSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "auth.forgotPassword",
      failureAction: resetPasswordUserFailed,
    });
  }
}

export function* authUserSaga() {
  yield takeEvery(resetPasswordUser.type, resetPasswordUserSaga);
  yield takeEvery(forgotPasswordUser.type, forgotPasswordUserSaga);
  yield takeEvery(fetchCompetitorsRegister.type, fetchCompetitorsRegisterSaga);
  yield takeEvery(registerUser.type, registerUserSaga);
  yield takeEvery(oAuthUser.type, oAuthUserSaga);
  yield takeEvery(authorizationUser.type, authorizationUserSaga);
  yield takeEvery(loginUser.type, loginUserSaga);
  yield takeEvery(updateUser.type, updateUserSaga);
  yield takeEvery(updatePasswordUser.type, updatePasswordUserSaga);
  yield takeEvery(fetchPaymentStatus.type, fetchPaymentStatusSaga);
  yield takeEvery(getStripeCustomerPortal.type, getStripeCustomerPortalSaga);
  yield takeEvery(logoutUser.type, logoutUserSaga);
  yield takeEvery(fetchRelatedBrands.type, fetchRelatedBrandsSaga);
  yield takeEvery(upgradeSubscription.type, upgradeSubscriptionSaga);
  yield takeEvery(refreshSession.type, refreshSessionSaga);
  yield takeEvery(fetchBrandRegister.type, fetchBrandRegisterSaga);
}
